import { useCallback, useEffect, useState } from 'react';

import { useApiClient } from '../providers/auth-provider';

export interface ConversationListItemData {
  id: string;
  title: string;
  preview: string | undefined;
  /** Raw ISO 8601 timestamp — formatted to relative time at render in the View layer */
  updated_at: string | undefined;
  /** Whether the agent is currently processing this conversation */
  processing_status: 'idle' | 'processing';
  /** Whether this conversation has unread messages (server-derived) */
  unread: boolean;
}

function toItem(c: {
  id: string;
  title: string;
  last_message_preview?: string | null;
  updated_at: string;
  processing_status?: 'idle' | 'processing';
  unread?: boolean;
}): ConversationListItemData {
  return {
    id: c.id,
    title: c.title || 'New Conversation',
    preview: c.last_message_preview ?? undefined,
    updated_at: c.updated_at,
    processing_status: c.processing_status ?? 'idle',
    unread: c.unread ?? false,
  };
}

export function useConversations() {
  const apiClient = useApiClient();
  const [conversations, setConversations] = useState<ConversationListItemData[]>([]);
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [error, setError] = useState<string | undefined>();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [fetchCount, setFetchCount] = useState(0);

  useEffect(() => {
    setStatus('loading');
    apiClient
      .listConversations({ limit: '25' })
      .then((res) => {
        setConversations(res.data.map(toItem));
        setHasMore(res.has_more);
        setStatus('success');
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'Failed to load');
        setStatus('error');
      });
  }, [apiClient, fetchCount]);

  const reload = useCallback(() => setFetchCount((n) => n + 1), []);

  const createConversation = useCallback(async (title?: string) => {
    try {
      const conv = await apiClient.createConversation(title ? { title } : {});
      setConversations((prev) => [toItem(conv), ...prev]);
      setSelectedId(conv.id);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to create conversation');
    }
  }, [apiClient]);

  const archiveConversation = useCallback(async (id: string) => {
    try {
      await apiClient.updateConversation(id, { status: 'archived' });
      setConversations((prev) => prev.filter((c) => c.id !== id));
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to archive conversation');
    }
  }, [apiClient]);

  const selectConversation = useCallback((id: string | null) => setSelectedId(id), []);

  const markRead = useCallback(async (id: string) => {
    // Optimistic update — set unread to false immediately
    setConversations((prev) => prev.map((c) => c.id === id ? { ...c, unread: false } : c));
    // Fire-and-forget API call
    apiClient.markConversationRead(id).catch(() => {});
  }, [apiClient]);

  const loadMore = useCallback(async () => {
    const last = conversations[conversations.length - 1];
    if (!last) return;
    try {
      const res = await apiClient.listConversations({ limit: '25', starting_after: last.id });
      setConversations((prev) => [...prev, ...res.data.map(toItem)]);
      setHasMore(res.has_more);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load more conversations');
    }
  }, [apiClient, conversations]);

  const refreshConversation = useCallback(async (id: string) => {
    try {
      const conv = await apiClient.getConversation(id);
      setConversations((prev) => prev.map((c) => c.id === id ? toItem(conv) : c));
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to refresh conversation');
    }
  }, [apiClient]);

  const updateConversationTitle = useCallback((id: string, title: string) => {
    setConversations((prev) =>
      prev.map((c) => c.id === id && c.title === 'New Conversation' ? { ...c, title } : c),
    );
  }, []);

  return { status, error, conversations, createConversation, archiveConversation, selectedId, selectConversation, hasMore, loadMore, refreshConversation, updateConversationTitle, markRead, reload };
}
